/**
 * @file Login.tsx
 * @description Standalone sign-in screen shown by App when OAuth login is enabled
 * (AUTH_MODE=oauth) and there is no session. "Zaloguj przez Google" is a full-page
 * link to /api/auth/google (Passport kicks off the OAuth flow); Apple is a disabled
 * placeholder until a passport-apple strategy is wired. Copy is Polish (instance
 * convention). After a successful Google sign-in the server redirects to "/", App
 * re-checks /api/auth/me and renders the dashboard.
 */

import { Activity } from "lucide-react";

/** Google "G" mark — lucide has no brand logos, so inline the official 4-color glyph. */
function GoogleMark() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

export function Login() {
  const denied =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("authError") === "denied";

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 p-4">
      <div className="card w-full max-w-sm p-8 text-center space-y-6 animate-fade-in">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-accent/15 flex items-center justify-center">
            <Activity className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-100">Hermes</h1>
            <p className="text-xs text-gray-500 mt-0.5">Zaloguj się, aby kontynuować</p>
          </div>
        </div>

        {denied && (
          <p className="text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
            Ten adres e-mail nie ma dostępu do Hermesa.
          </p>
        )}

        <div className="space-y-2.5">
          <a
            href="/api/auth/google"
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-lg bg-white text-gray-800 font-medium text-sm px-4 py-2.5 hover:bg-gray-100 transition-colors"
          >
            <GoogleMark />
            Zaloguj przez Google
          </a>
          <button
            type="button"
            disabled
            title="Logowanie przez Apple — wkrótce"
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-lg border border-border bg-surface-2 text-gray-500 font-medium text-sm px-4 py-2.5 cursor-not-allowed opacity-60"
          >
            Zaloguj przez Apple
            <span className="text-[10px] font-normal">(wkrótce)</span>
          </button>
        </div>

        <p className="text-[11px] text-gray-600">Dostęp tylko dla autoryzowanych adresów e-mail.</p>
      </div>
    </div>
  );
}
